import { Injectable, inject } from '@angular/core';
import { from } from 'rxjs';
import { map } from 'rxjs/operators';
import { FeatureService, PermissionService } from '../../../../../core/services/permission';
import { StoresService } from '../../../../../core/services/stores';
import { Customer } from '../../../../../data/models';
import { RepoClientsService } from '../../../../../data/repositories/repo-clients.service';
import { RepoCommonService } from '../../../../../data/repositories/repo-common.service';
import { BreezeCase } from '../../../../../utils/types/convert-case';
import { NavMenuItem } from '../menubar/domain/entities/menu-item.model';
import { SecondaryNavStoreService } from '../secondary-nav.store.service';

@Injectable({
    providedIn: 'root',
})
export class SecondaryNavClientService {
    #secNavStore = inject(SecondaryNavStoreService);
    #featureService = inject(FeatureService);
    #permissionService = inject(PermissionService);
    #repoCommon = inject(RepoCommonService);
    #storeService = inject(StoresService);
    #navState = inject(SecondaryNavStoreService);
    #clientsRepo = inject(RepoClientsService);

    getMenuBarData(): NavMenuItem[] {
        const client = this.#secNavStore.currentClient.value();
        if (!client) return [];
        return [
            {
                description: 'Client Details',
                showMenuItem:
                    this.#permissionService.isAvailable('customers.maintain-customers.customer-details') &&
                    this.#featureService.isStateAvailable('customers.view.details'),
                route: {
                    path: ['/customers', 'list', client.ID_Customer, 'details'],
                },
            },
            {
                description: 'Leads',
                showMenuItem:
                    this.#featureService.isStateAvailable('leads') &&
                    this.#permissionService.isAvailable('leads.maintain-leads.view-leads') &&
                    this.#featureService.isStateAvailable('leads'),
                route: {
                    path: ['/customers', 'list', client.ID_Customer, 'leads'],
                },
            },
            {
                description: 'Estimates',
                showMenuItem:
                    this.#permissionService.isAvailable('customers.estimates-access.estimates-menu') &&
                    this.#featureService.isStateAvailable('customers.view.estimates-v2'),
                route: {
                    path: ['/customers', 'list', client.ID_Customer, 'estimates-v2'],
                },
            },
            {
                description: 'Jobs',
                showMenuItem: !this.#featureService.isAvailable('compact-jobs'),
                route: {
                    path: ['/customers', 'list', client.ID_Customer, 'jobs'],
                },
            },
            {
                description: 'Communications',
                showMenuItem:
                    this.#permissionService.isAvailable('customers.messages-access.messages-menu') &&
                    this.#featureService.isStateAvailable('customers.view.messages'),
                route: {
                    path: ['/customers', 'list', client.ID_Customer, 'messages'],
                },
            },
            {
                description: 'Reminders',
                showMenuItem:
                    this.#permissionService.isAvailable('customers.maintain-reminders.reminders-menu') &&
                    this.#featureService.isStateAvailable('customers.view.timeline') &&
                    this.#featureService.isStateAvailable('customers.maintain-customers.set-customer-reminder'),
                route: {
                    path: ['/customers', 'list', client.ID_Customer, 'timeline'],
                },
            },
            {
                description: 'Connection Management',
                showMenuItem: this.#featureService.isAvailable('merchant') || this.#featureService.isAvailable('manufacturer'),
                route: {
                    path: ['/customers', 'list', client.ID_Customer, 'manage-connection'],
                },
            },
        ];
    }

    getCustomerTypes() {
        return from(this.#repoCommon.GetCustomerTypes()).pipe(
            map((res) => {
                return res.map((x) => x.Description).sort();
            })
        );
    }

    saveClient(client: BreezeCase<Customer>) {
        return this.#clientsRepo.Save(null, 'Client saved').then((result) => {
            this.#storeService.refreshCustomers();
            this.#navState.currentClient.set(client);
            return result;
        });
    }
}
